import React, { useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Avatar } from '../../../../components/ui/Avatar'
import { ActionButton } from './ActionButton'
import { AgentHoverCard } from './AgentHoverCard'
import { Copy, ThumbsUp, ThumbsDown, Check, FileText } from 'lucide-react'

import type { Message } from '../../../../types/chat.types'
import type { Agent } from '../../../../services/agentService'

interface ChatMessageProps {
    message: Message
    index: number
    agentName: string
    agentAvatar?: string | null
    agent?: Agent | null
    onCopy?: (content: string, index: number) => void
    onFeedback?: (index: number, feedback: 'liked' | 'disliked') => void

    copiedMessageId?: number | null
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
    message,
    index,
    agentName,
    agentAvatar,
    agent,
    onCopy,
    onFeedback,

    copiedMessageId
}) => {
    const isUser = message.role === 'user'
    const isAssistant = message.role === 'assistant'
    const navigate = useNavigate()
    const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 })
    const [isHoveringName, setIsHoveringName] = useState(false)
    const anchorRef = useRef<HTMLDivElement>(null)

    const handleMouseEnter = () => {
        if (!agent || !anchorRef.current) return

        const rect = anchorRef.current.getBoundingClientRect()
        setHoverPos({
            x: rect.left,
            y: rect.bottom + 4
        })
        setIsHoveringName(true)
    }

    const handleMouseLeave = () => {
        setIsHoveringName(false)
    }

    const handleProfileClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        // Use agent handle/name if available, otherwise fallback to agentName prop
        const nameToUse = agent?.handle || agent?.name || agentName
        if (nameToUse) {
            const formattedHandles = nameToUse.toLowerCase().replace(/\s+/g, '')
            navigate(`/agentes/${formattedHandles}`)
        }
    }

    // Render user message
    if (isUser) {
        return (
            <motion.div
                {...({
                    initial: { opacity: 0, y: 10 },
                    animate: { opacity: 1, y: 0 },
                    exit: { opacity: 0 }
                } as any)}
                className="flex justify-end p-4"
            >
                <div className="max-w-[80%]">
                    {/* Text content */}
                    {message.content && message.content.trim() && (
                        <div className="bg-[#eff3f4] dark:bg-gray-700 text-gray-900 dark:text-white rounded-[20px] rounded-tr-sm px-4 py-2">
                            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        </div>
                    )}

                    {/* Attachments */}
                    {message.attachments && message.attachments.length > 0 && (
                        <div className={message.content && message.content.trim() ? "mt-2" : ""}>
                            {/* Images Grid */}
                            {message.attachments.some(att => att.type.startsWith('image/')) && (
                                <div className="flex flex-wrap gap-2 mb-2">
                                    {message.attachments.filter(att => att.type.startsWith('image/')).map((att, i) => (
                                        <div key={i} className="relative max-h-64 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800">
                                            {att.data && (
                                                <img
                                                    src={att.data}
                                                    alt={att.name}
                                                    className="max-h-64 w-auto object-contain"
                                                    title={att.name}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Other Files */}
                            {message.attachments.some(att => !att.type.startsWith('image/')) && (
                                <div className="space-y-2">
                                    {message.attachments.filter(att => !att.type.startsWith('image/')).map((att, i) => (
                                        <div key={i} className="flex items-center gap-3 bg-white/60 dark:bg-gray-800/60 rounded-lg p-2 border border-gray-200 dark:border-gray-700">
                                            <div className="flex-shrink-0 w-10 h-10 rounded overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                                                <FileText className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                                                    {att.name}
                                                </div>
                                                {att.size && (
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                                        {(att.size / 1024).toFixed(1)} KB
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Timestamp */}
                    {message.timestamp && (
                        <span className="text-xs text-gray-500 mt-1 block">
                            {message.timestamp}
                        </span>
                    )}
                </div>
            </motion.div>
        )
    }

    // Render assistant message
    if (isAssistant) {
        return (
            <motion.div
                {...({
                    initial: { opacity: 0, y: 20 },
                    animate: { opacity: 1, y: 0 },
                    exit: { opacity: 0 }
                } as any)}
                className="w-full pr-0"
            >
                <div className="flex gap-3">
                    {/* Avatar */}
                    <div
                        className="flex-shrink-0 h-fit"
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                    >
                        <Avatar
                            size="md"
                            alt={agentName}
                            src={agentAvatar || undefined}
                            onClick={handleProfileClick}
                            className="cursor-pointer"
                        />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                        {/* Header with name and timestamp */}
                        <div className="mb-1 relative">
                            {/* Hover Card */}
                            {/* Hover Card using Portal */}
                            {isHoveringName && agent && (
                                createPortal(
                                    <div
                                        style={{
                                            position: 'fixed',
                                            top: hoverPos.y,
                                            left: hoverPos.x,
                                            zIndex: 999999,
                                        }}
                                        onMouseEnter={() => setIsHoveringName(true)}
                                        onMouseLeave={handleMouseLeave}
                                    >
                                        <motion.div
                                            {...({
                                                initial: { opacity: 0, scale: 0.9, y: 10 },
                                                animate: { opacity: 1, scale: 1, y: 0 },
                                                exit: { opacity: 0, scale: 0.9, y: 10 }
                                            } as any)}
                                            transition={{ type: "spring", damping: 20, stiffness: 300 }}
                                        >
                                            <AgentHoverCard agent={agent} showOptions={false} />
                                        </motion.div>
                                    </div>,
                                    document.body
                                )
                            )}

                            {/* Row 1: Name and Badge */}
                            <div
                                ref={anchorRef}
                                className="flex items-center gap-1 w-fit"
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                            >
                                <span
                                    className="font-bold text-gray-900 dark:text-white text-[15px] hover:underline cursor-pointer"
                                    onClick={handleProfileClick}
                                >
                                    {agentName}
                                </span>
                                {/* Verified badge */}
                                <svg
                                    className="w-[18px] h-[18px] cursor-pointer"
                                    viewBox="0 0 22 22"
                                    aria-label="Verified account"
                                    role="img"
                                    onClick={handleProfileClick}
                                >
                                    <g>
                                        <path fill="#1d9bf0" d="M20.396 11c-.018-.646-.215-1.275-.57-1.816-.354-.54-.852-.972-1.438-1.246.223-.607.27-1.264.14-1.897-.131-.634-.437-1.218-.882-1.687-.47-.445-1.053-.75-1.687-.882-.633-.13-1.29-.083-1.897.14-.273-.587-.704-1.086-1.245-1.44S11.647 1.62 11 1.604c-.646.017-1.273.213-1.813.568s-.969.854-1.24 1.44c-.608-.223-1.267-.272-1.902-.14-.635.13-1.22.436-1.69.882-.445.47-.749 1.055-.878 1.688-.13.633-.08 1.29.144 1.896-.587.274-1.087.705-1.443 1.245-.356.54-.555 1.17-.574 1.817.02.647.218 1.276.574 1.817.356.54.856.972 1.443 1.245-.224.606-.274 1.263-.144 1.896.13.634.433 1.218.877 1.688.47.443 1.054.747 1.687.878.633.132 1.29.084 1.897-.136.274.586.705 1.084 1.246 1.439.54.354 1.17.551 1.816.569.647-.016 1.276-.213 1.817-.567s.972-.854 1.245-1.44c.604.239 1.266.296 1.903.164.636-.132 1.22-.447 1.68-.907.46-.46.776-1.044.908-1.681s.075-1.299-.165-1.903c.586-.274 1.084-.705 1.439-1.246.354-.54.551-1.17.569-1.816zM9.662 14.85l-3.429-3.428 1.293-1.302 2.072 2.072 4.4-4.794 1.347 1.246z"></path>
                                    </g>
                                </svg>
                            </div>

                            {/* Row 2: Handle and Time */}
                            <div className="flex items-center gap-2">
                                <span
                                    className="text-gray-500 dark:text-gray-400 text-sm cursor-pointer hover:underline"
                                    onMouseEnter={handleMouseEnter}
                                    onMouseLeave={handleMouseLeave}
                                    onClick={handleProfileClick}
                                >
                                    @{agentName.toLowerCase().replace(/\s+/g, '')}
                                </span>
                                <span className="text-gray-500 dark:text-gray-400 text-sm hover:underline cursor-pointer">
                                    {message.timestamp || (message.created_at ? new Date(message.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '')}
                                </span>
                            </div>
                        </div>

                        {/* Message content */}
                        <div className="text-[14px] text-gray-900 dark:text-gray-100 whitespace-pre-wrap leading-normal">
                            {message.isHtml ? (
                                <div
                                    className="prose prose-sm max-w-none dark:prose-invert"
                                    dangerouslySetInnerHTML={{ __html: message.content }}
                                />
                            ) : (
                                message.content
                            )}
                        </div>

                        {/* Action buttons */}
                        <div className="flex items-center gap-1 mt-2">

                            {onCopy && (
                                <ActionButton
                                    icon={copiedMessageId === index ? Check : Copy}
                                    label={copiedMessageId === index ? "Copiado!" : "Copiar"}
                                    onClick={() => onCopy(message.content, index)}
                                    isActive={copiedMessageId === index}
                                    activeColor="text-green-600"
                                    hoverColor="text-green-500"
                                />
                            )}
                            {onFeedback && (
                                <>
                                    <ActionButton
                                        icon={ThumbsUp}
                                        label="Gostei"
                                        onClick={() => onFeedback(index, 'liked')}
                                        isActive={message.feedback === 'liked'}
                                        hoverColor="text-pink-600"
                                    />
                                    <ActionButton
                                        icon={ThumbsDown}
                                        label="Não gostei"
                                        onClick={() => onFeedback(index, 'disliked')}
                                        isActive={message.feedback === 'disliked'}
                                        hoverColor="text-orange-500"
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </motion.div>
        )
    }

    // System message (fallback)
    return (
        <motion.div
            {...({
                initial: { opacity: 0 },
                animate: { opacity: 1 }
            } as any)}
            className="text-center text-xs text-secondary-text p-2"
        >
            {message.content}
        </motion.div>
    )
}
